import {
  Component,
  EventEmitter,
  Input,
  Output,
  AfterViewInit,
} from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { StripeService } from "app/services/stripe.service";
import {
  loadStripe,
  Stripe,
  StripeCardNumberElement,
  StripeCardExpiryElement,
  StripeCardCvcElement,
} from "@stripe/stripe-js";
import { environment } from "environments/environment";

@Component({
  selector: "app-stripe-payment",
  templateUrl: "./stripe-payment.component.html",
  styleUrls: ["./stripe-payment.component.scss"],
})
export class StripePaymentComponent implements AfterViewInit {
  @Input() invoice: any;
  @Input() parentId: string = "";
  @Input() parentEmail: string = "";
  @Output() paymentSuccess = new EventEmitter<any>();
  @Output() paymentFailure = new EventEmitter<string>();

  stripe: Stripe | null = null;
  cardNumber!: StripeCardNumberElement;
  cardExpiry!: StripeCardExpiryElement;
  cardCvc!: StripeCardCvcElement;
  cardMounted = false;
  paymentProcessing = false;

  paymentForm = this.fb.group({
    email: ["", [Validators.required, Validators.email]],
    postalCode: [""],
  });

  constructor(private fb: FormBuilder, private stripeService: StripeService) {}

  ngOnInit(): void {
    if (this.parentEmail) {
      this.paymentForm.patchValue({ email: this.parentEmail });
    }
  }

  async ngAfterViewInit() {
    this.stripe = await loadStripe(environment.stripePublishableKey);

    if (this.stripe && !this.cardMounted) {
      const elements = this.stripe.elements();

      this.cardNumber = elements.create("cardNumber");
      this.cardNumber.mount("#card-number-element");

      this.cardExpiry = elements.create("cardExpiry");
      this.cardExpiry.mount("#card-expiry-element");

      this.cardCvc = elements.create("cardCvc");
      this.cardCvc.mount("#card-cvc-element");

      this.cardMounted = true;
    }
  }

  async submitPayment() {
    if (this.paymentForm.invalid || this.paymentProcessing || !this.stripe)
      return;

    this.paymentProcessing = true;

    try {
      const paymentIntentResponse = await this.stripeService
        .createPaymentIntent(
          this.invoice.Program_Fee__c,
          this.invoice.Id,
          this.parentId
        )
        .toPromise();
      const clientSecret = paymentIntentResponse?.clientSecret;
      if (!clientSecret)
        throw new Error("❌ No clientSecret received from server");

      const { paymentIntent, error } = await this.stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: this.cardNumber,
            billing_details: {
              email: this.paymentForm.value.email?.trim(),
              address: {
                postal_code: this.paymentForm.value.postalCode?.trim(),
              },
            },
          },
        }
      );

      if (error) {
        this.paymentFailure.emit(error.message || "Payment failed");
        this.paymentProcessing = false;
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        // ✅ Call the new endpoint for successful payments
        await this.stripeService
          .notifyPaymentSuccess(
            paymentIntent.id,
            this.invoice.Program_Fee__c,
            this.invoice.Id,
            this.parentId
          )
          .toPromise();

        this.paymentSuccess.emit(paymentIntent);
      } else {
        this.paymentFailure.emit("Payment could not be completed");
        this.paymentProcessing = false;
      }
    } catch (err: any) {
      this.paymentFailure.emit(err.message || "Payment processing error");
      this.paymentProcessing = false;
    }
  }
}
